import { Pic } from "./Pic";

import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

export const CRITERIA = [
  ["Sicurezza sismica", "Il legno è leggero e duttile: per questo le case in legno si comportano bene in caso di terremoto. Cambia però il modo in cui la struttura lavora.", "Buona: le pareti a telaio, controventate dai pannelli, resistono bene, ma la risposta dipende da moltissimi collegamenti.", "Eccellente: pareti e solai massicci formano una scatola rigida e monolitica che distribuisce le forze in modo uniforme."],
  ["Isolamento e ponti termici", "La continuità dell'isolamento decide quanto la casa consuma e se rischia condense e muffe.", "Buono, ma ogni montante interrompe l'isolante: i ponti termici vanno curati uno a uno.", "Superiore: il pannello portante è continuo e l'isolamento lo avvolge senza interruzioni."],
  ["Comfort estivo e acustico", "La massa delle pareti influenza il fresco d'estate e il silenzio in casa.", "Pareti leggere: servono strati aggiuntivi per rallentare il calore estivo e isolare dai rumori.", "Il legno massiccio accumula calore e attenua i rumori: più fresco d'estate, più silenzioso tutto l'anno."],
  ["Tempi e costi di produzione", "Il numero di pezzi e di lavorazioni pesa direttamente sul prezzo finale.", "Più componenti e più lavorazioni, in fabbrica e in cantiere.", "Pannelli unici tagliati a controllo numerico nel nostro stabilimento: meno pezzi, meno ore, costi di produzione ridotti."],
  ["Impianti e modifiche", "Dove passano tubi e cavi va deciso con anticipo, in entrambi i sistemi.", "Le cavità tra i montanti facilitano il passaggio degli impianti.", "Richiede una progettazione integrata: ogni passaggio va previsto prima della produzione, e il nostro ufficio tecnico lo fa per te."],
];

const Criterion = ({ c, i, t }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="grid gap-6 border-t border-[#121110]/15 pt-10 lg:grid-cols-12"
    data-testid={`choose-criterion-${i}`}
  >
    <div className="lg:col-span-4">
      <span className="font-display text-5xl font-bold text-[#C26D38]">{String(i + 1).padStart(2, "0")}</span>
      <h3 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">{t(`choose.c${i}.t`, c[0])}</h3>
      <p className="mt-3 text-base leading-relaxed text-[#6E685E]">{t(`choose.c${i}.d`, c[1])}</p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
      <div className="border border-[#121110]/15 p-6" data-testid={`choose-frame-${i}`}>
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]"><Minus className="h-4 w-4" /> {t("choose.frame", "Telaio")}</p>
        <p className="mt-3 text-base leading-relaxed text-[#3D3833]">{t(`choose.c${i}.f`, c[2])}</p>
      </div>
      <div className="bg-[#121110] p-6 text-[#F7F5F0]" data-testid={`choose-xlam-${i}`}>
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]"><Check className="h-4 w-4" /> {t("choose.xlam", "X-LAM Emma")}</p>
        <p className="mt-3 text-base leading-relaxed text-[#E4DED3]">{t(`choose.c${i}.x`, c[3])}</p>
      </div>
    </div>
  </motion.article>
);

const When = ({ dark, title, text, testid }) => (
  <div className={`p-8 sm:p-10 ${dark ? "bg-[#C26D38] text-[#121110]" : "border border-[#121110]/20"}`} data-testid={testid}>
    <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h3>
    <p className={`mt-4 text-base leading-relaxed ${dark ? "text-[#121110]/85" : "text-[#6E685E]"}`}>{text}</p>
  </div>
);

export const ChooseGuide = () => {
  const t = useT();
  return (
    <section id="xlam-o-telaio" className="bg-[#F7F5F0] px-5 py-24 text-[#121110] sm:px-8 lg:py-32" data-testid="choose-guide">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="02">{t("choose.label", "Guida tecnica")}</SectionLabel>
            <MaskLines
              className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
              lines={[t("choose.h1", "Come scegliere"), <span key="b">{t("choose.h2a", "tra X-LAM e")} <em className="font-serif font-normal text-[#C26D38]">{t("choose.h2em", "telaio.")}</em></span>]}
            />
            <Reveal delay={0.2} className="mt-8 max-w-2xl text-base leading-relaxed text-[#6E685E] sm:text-lg">
              {t("choose.intro", "Entrambi i sistemi permettono di costruire case in legno sicure ed efficienti, ma non sono uguali. Ecco i 5 criteri che usiamo con i nostri clienti per scegliere il sistema giusto.")}
            </Reveal>
          </div>
          <Reveal className="grid grid-cols-2 gap-3 lg:col-span-5" delay={0.15}>
            <Pic src="/img/emma/telaio-sezione-casa.jpg" alt={t("sys.fimg0", "Casa a telaio (Platform Frame) vista in sezione")} loading="lazy" className="aspect-[4/3] w-full bg-white object-contain" />
            <Pic src="/img/emma/xlam-sezione-casa.jpg" alt={t("sys.img0", "Casa in X-LAM Emma vista in sezione")} loading="lazy" className="aspect-[4/3] w-full bg-white object-contain" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]">{t("choose.frame", "Telaio")}</span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#C26D38]">{t("choose.xlam", "X-LAM Emma")}</span>
          </Reveal>
        </div>
        <div className="mt-20 space-y-12">
          {CRITERIA.map((c, i) => <Criterion key={c[0]} c={c} i={i} t={t} />)}
        </div>
        <div className="mt-20 grid gap-6 lg:grid-cols-2">
          <When title={t("choose.wf.t", "Quando può avere senso il telaio")} text={t("choose.wf.d", "Costruzioni molto leggere, sopraelevazioni su edifici esistenti con limiti di carico, piccoli ampliamenti e annessi.")} testid="choose-when-frame" />
          <When dark title={t("choose.wx.t", "Quando scegliere l'X-LAM Emma")} text={t("choose.wx.d", "Abitazioni principali, edifici su più piani, zone sismiche e per chi cerca il massimo comfort termico e acustico con un costo di produzione più contenuto.")} testid="choose-when-xlam" />
        </div>
        <Reveal className="mt-12 max-w-3xl">
          <p className="font-display text-2xl font-bold leading-tight sm:text-3xl" data-testid="choose-verdict">
            {t("choose.verdict", "Il nostro consiglio: per la casa in cui vivrai, scegli l'X-LAM.")} <em className="font-serif font-normal text-[#C26D38]">{t("choose.verdictEm", "Ma la scelta giusta si fa sul tuo progetto: parlane con il nostro ufficio tecnico.")}</em>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
